import { toast } from "react-toastify";
import { GetMyInvitesQuery, GetMyNotificationsQuery } from "~/@graphql/types";
import { timeAgo } from "~/utils/belt";
import { Button } from "./Button";
import {
  markAllNotificationsAsRead,
  markNotificationAsRead,
} from "~/helpers/notifications";
import { useEffect, useState } from "react";
import { acceptInvite, rejectInvite } from "~/helpers/invites";
import { useFetcher } from "react-router";

function formatDate(date: Date) {
  const options = {
    weekday: "long",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    day: "2-digit",
    month: "short",
  };
  return date.toLocaleString("en-US", options as Intl.DateTimeFormatOptions);
}

const NotificationsDrawer = ({
  _notifications,
  toggleNotificationsPanel,
  refetchNotifications,
  minNotificationDate,
}: {
  _notifications: GetMyNotificationsQuery["party_notifications"];
  toggleNotificationsPanel: () => void;
  refetchNotifications: () => void;
  minNotificationDate: string | null;
}) => {
  const invitesFetcher = useFetcher<GetMyInvitesQuery>();
  const [filterNotifications, setFilterNotifications] =
    useState<boolean>(false);
  const [isSubmitting, setSubmitting] = useState<boolean>(false);
  const [justAccepted, setJustAccepted] = useState<boolean>(false);

  useEffect(() => {
    if (minNotificationDate) {
      invitesFetcher.load(`/profile/my-invites?minDate=${minNotificationDate}`);
    }
  }, [minNotificationDate]);

  const notifications = _notifications.map(
    ({ id, metadata, notification, created_at, read, read_at, sender }) => {
      return {
        id,
        read,
        read_at,
        title: notification.title,
        message: notification.body,
        time: formatDate(new Date(created_at)),
        ago: timeAgo(new Date(created_at)),
        type: notification.type,
        sender,
        metadata: notification.metadata,
        partyNotificationMetadata: metadata,
      };
    }
  );

  const teamInvitesCount = notifications.filter(
    (n) => n.type.value === "TEAM_INVITE"
  ).length;

  const invitesStatus =
    invitesFetcher.state === "idle" && invitesFetcher.data
      ? invitesFetcher.data.invites
          .map((invite) => ({
            [invite.invite_code]: invite?.party_invite?.status.value,
          }))
          .reduce((acc, val) => ({ ...acc, ...val }), {})
      : {};

  return (
    <div className="w-full md:w-auto p-6 pt-10 lg:ml-[17.375rem] lg:p-12 bg-jiwe-red absolute h-screen top-0 left-0 z-50">
      <button
        type="button"
        className="items-center text-[#fff] -mt-6 mb-6 gap-1 h-8 -ml-1"
        onClick={() => toggleNotificationsPanel()}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
          className="text-[#fff] size-8"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
          />
        </svg>
      </button>
      <div className="lg:w-96 overflow-scroll max-h-[92.5%]">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 border-b border-b-[#FFDAB9]">
          <h2 className="text-lg font-semibold text-[#FFDAB9]">
            Your notifications
          </h2>
          <button
            className="text-[#FFDAB9]"
            onClick={async () => {
              const result = await markAllNotificationsAsRead();
              if (!result.errors) {
                // Refetch notifications
                refetchNotifications();
              }
            }}
          >
            Mark all as read
          </button>
        </div>

        {/* Tabs */}
        <div className="mb-6 text-sm">
          <div className="flex gap-x-4">
            <button
              className="text-[#fff] uppercase"
              type="button"
              onClick={() => {
                setFilterNotifications(false);
              }}
              style={{
                opacity: filterNotifications ? 0.6 : 1,
              }}
            >
              View all
            </button>
            <button
              className="text-[#fff] uppercase"
              type="button"
              onClick={() => {
                setFilterNotifications(true);
              }}
              style={{
                opacity: filterNotifications ? 1 : 0.6,
              }}
            >
              Team Invites{" "}
              <span className="font-semibold">({teamInvitesCount})</span>
            </button>
          </div>
        </div>

        {/* Notifications List */}
        <div className="space-y-4 text-[#FFF]">
          {notifications
            .filter((notifications) => {
              if (filterNotifications) {
                return notifications.type.value === "TEAM_INVITE";
              }
              return true;
            })
            .map((notification) => (
              <div
                key={notification.id}
                className="flex flex-col items-left pb-5 mb-4 border-b border-b-[#3b0212] relative"
              >
                {/* Unread dot */}
                {!notification?.read && (
                  <button
                    type="button"
                    onClick={async () => {
                      const result = await markNotificationAsRead(
                        notification.id
                      );
                      if (!result.errors) {
                        // Refetch notifications
                        refetchNotifications();
                      }
                    }}
                  >
                    <span className="absolute top-2 -left-5 w-2 h-2 bg-[#46ff03] rounded-full"></span>
                  </button>
                )}

                <div className="flex-1">
                  <p
                    className="font-semibold"
                    style={{
                      fontWeight: !notification?.read ? "bold" : "normal",
                    }}
                  >
                    {notification.message}
                  </p>
                  <p className="mt-2 text-sm text-[#d0d0d0]">
                    {notification.time}
                  </p>
                </div>
                {notification.type.value === "TEAM_INVITE" && (
                  <div className="mt-[.125rem] text-xs text-[#d0d0d0]">
                    {notification.ago}
                  </div>
                )}
                {notification.type.value === "TEAM_INVITE" &&
                  invitesStatus[
                    notification.partyNotificationMetadata.inviteCode
                  ] &&
                  invitesStatus[
                    notification.partyNotificationMetadata.inviteCode
                  ] !== "ACCEPTED" &&
                  !justAccepted && (
                    <div className="flex gap-x-4 md:gap-x-2">
                      <Button
                        type="button"
                        size="small"
                        className="bg-jiwe-dark-blue !text-[#fff] text-xs !md:text-[.7rem] !px-2 !py-1 disabled:!bg-grey-light rounded-lg disabled:cursor-not-allowed"
                        disabled={isSubmitting}
                        onClick={async () => {
                          setSubmitting(true);
                          const res = await rejectInvite(
                            notification.partyNotificationMetadata.inviteCode
                          );
                          setSubmitting(false);
                          if (res?.errors) {
                            toast.error(res?.errors[0].message);
                          } else {
                            toast.success(res.message);
                            refetchNotifications();
                          }
                          setJustAccepted(true);
                          await markNotificationAsRead(notification.id);
                        }}
                      >
                        Decline
                      </Button>
                      <Button
                        type="button"
                        size="small"
                        className="bg-[#2e8b57] !text-[#fff] text-xs !md:text-[.7rem] !px-2 !py-1 disabled:!bg-grey-light rounded-lg disabled:cursor-not-allowed"
                        disabled={isSubmitting}
                        onClick={async () => {
                          setSubmitting(true);
                          const res = await acceptInvite(
                            notification.partyNotificationMetadata.inviteCode
                          );
                          setSubmitting(false);
                          if (res?.errors) {
                            toast.error(res?.errors[0].message);
                          } else {
                            toast.success(res.message);
                            refetchNotifications();
                          }
                          setJustAccepted(true);
                          await markNotificationAsRead(notification.id);
                        }}
                      >
                        Accept
                      </Button>
                    </div>
                  )}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default NotificationsDrawer;
